import { assertSafeInt, invariant } from "./assert";

declare const brand: unique symbol;

/** Nominal type: a `T` that cannot be mixed with another brand by accident. */
export type Brand<T, B extends string> = T & { readonly [brand]: B };

/** Money in euro cents (or the pack currency's minor unit). */
export type Cents = Brand<number, "Cents">;
/** Game time in seconds since the start of the game. */
export type GameTime = Brand<number, "GameTime">;
/** Duration in seconds. */
export type Seconds = Brand<number, "Seconds">;
export type Meters = Brand<number, "Meters">;
export type Kg = Brand<number, "Kg">;
export type Milliliters = Brand<number, "Milliliters">;
/** Cargo volume in litres (dm³): 1 m³ = 1 000 L. */
export type Liters = Brand<number, "Liters">;
/** Basis points: 10 000 bps = 100 %. */
export type Bps = Brand<number, "Bps">;

export const BPS_ONE = 10_000 as Bps;

function int<T extends number>(value: number, label: string): T {
  assertSafeInt(value, label);
  // Normalise -0 so that canonical serialisation never sees it.
  return (value === 0 ? 0 : value) as T;
}

export const cents = (value: number): Cents => int<Cents>(value, "Cents");
export const seconds = (value: number): Seconds => int<Seconds>(value, "Seconds");
export const meters = (value: number): Meters => int<Meters>(value, "Meters");
export const kg = (value: number): Kg => int<Kg>(value, "Kg");
export const milliliters = (value: number): Milliliters => int<Milliliters>(value, "Milliliters");
export const liters = (value: number): Liters => int<Liters>(value, "Liters");
export const bps = (value: number): Bps => int<Bps>(value, "Bps");

export function gameTime(value: number): GameTime {
  const t = int<GameTime>(value, "GameTime");
  invariant(t >= 0, `GameTime must be >= 0, got ${t}`);
  return t;
}

export const MINUTE = 60 as Seconds;
export const HOUR = 3_600 as Seconds;
export const DAY = 86_400 as Seconds;

/** Identifiers: opaque strings, one brand per entity kind. */
export type Id<K extends string> = Brand<string, `Id:${K}`>;
export type CompanyId = Id<"Company">;
export type ContractId = Id<"Contract">;
export type TripId = Id<"Trip">;
export type VehicleId = Id<"Vehicle">;
export type ShipmentId = Id<"Shipment">;
export type NodeId = Id<"Node">;
export type EdgeId = Id<"Edge">;
export type HazardId = Id<"Hazard">;

const ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_.:-]*$/;

/** Builds an identifier; restricted alphabet so ids sort and serialise predictably. */
export function id<K extends string>(value: string): Id<K> {
  invariant(ID_PATTERN.test(value), `invalid identifier: ${JSON.stringify(value)}`);
  return value as Id<K>;
}
